import { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { navigate } from '@xing-com/crate-core-router';
import { XING_JOBS_SEARCH_KI_PATH } from '@xing-com/crate-core-routes';
import { AI_SEARCH_FEEDBACK_SURVEY_LO_SWIMP_PARAM } from '@xing-com/crate-jobs-constants';
import { scale150, spaceM, spaceXL, spaceXS } from '@xing-com/tokens';
import { BodyCopy, Headline } from '@xing-com/typography';

import { LoadingIllustration } from '../assets/loading-illustration';
import { POLL_INTERVAL_MS, TIMEOUT_MS } from '../constants';
import { useFabiAIGeneration } from '../hooks/use-fabi-ai-generation';

import { ErrorScreen } from './error-screen';

type Props = {
  role: string;
  company: string;
  backToInput: () => void;
};

export const LoadingScreen: React.FC<Props> = ({
  role,
  company,
  backToInput,
}) => {
  const [triggerFabiAIGeneration, { data, loading, errorType }] =
    useFabiAIGeneration(TIMEOUT_MS, POLL_INTERVAL_MS);

  const handleTriggerGetPrompt = useCallback(() => {
    triggerFabiAIGeneration(role, company);
  }, [triggerFabiAIGeneration, company, role]);

  useEffect(() => {
    handleTriggerGetPrompt();
  }, [handleTriggerGetPrompt]);

  useEffect(() => {
    if (data) {
      const params = new URLSearchParams({
        keywords: data.query,
        [AI_SEARCH_FEEDBACK_SURVEY_LO_SWIMP_PARAM]: '1',
      });

      navigate(`${XING_JOBS_SEARCH_KI_PATH}?${params.toString()}`);
    }
  }, [data]);

  return (
    <Container>
      {!loading && errorType ? (
        <ErrorScreen
          refetchGetPrompt={handleTriggerGetPrompt}
          errorType={errorType}
          backToInput={backToInput}
        />
      ) : (
        <>
          <Illustration />
          <TextWrapper>
            <Headline sizeConfined="large" noMargin>
              <FormattedMessage id="LOSP_SWIMP_LOADING_TITLE" />
            </Headline>
            <BodyCopy size="small" sizeConfined="medium" noMargin>
              <FormattedMessage id="LOSP_SWIMP_LOADING_SUBTITLE" />
            </BodyCopy>
          </TextWrapper>
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spaceXL};
  height: calc(100dvh - ${scale150} + ${spaceM});
  margin-top: calc(-${scale150} - ${spaceM});
`;

const Illustration = styled(LoadingIllustration)`
  .sparkle {
    transform: scale(0);
  }

  #sparkle01 {
    transform-box: fill-box;
    transform-origin: center;
    animation: sparklePulse 3.2s ease-in-out infinite;
    animation-delay: 0s;
  }

  #sparkle02 {
    transform-box: fill-box;
    transform-origin: center;
    animation: sparklePulse 2.8s ease-in-out infinite;
    animation-delay: 0.18s;
  }

  #sparkle03 {
    transform-box: fill-box;
    transform-origin: center;
    animation: sparklePulse 3s ease-in-out infinite;
    animation-delay: 0.37s;
  }

  #sparkle04 {
    transform-box: fill-box;
    transform-origin: center;
    animation: sparklePulse 2.9s ease-in-out infinite;
    animation-delay: 0.61s;
  }

  #sparkle05 {
    transform-box: fill-box;
    transform-origin: center;
    animation: sparklePulse 3.1s ease-in-out infinite;
    animation-delay: 0.79s;
  }

  #sparkle06 {
    transform-box: fill-box;
    transform-origin: center;
    animation: sparklePulse 2.5s ease-in-out infinite;
    animation-delay: 0.93s;
  }

  #sparkle07 {
    transform-box: fill-box;
    transform-origin: center;
    animation: sparklePulse 2.7s ease-in-out infinite;
    animation-delay: 1.27s;
  }

  @keyframes sparklePulse {
    0% {
      transform: scale(0);
    }
    25% {
      transform: scale(0);
    }
    30% {
      transform: scale(1);
    }
    35% {
      transform: scale(1);
    }
    45% {
      transform: scale(0);
    }
    100% {
      transform: scale(0);
    }
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spaceXS};
`;
